import { Cookie } from '../types'

// ─── Shopify Storefront API Configuration ───
const SHOPIFY_DOMAIN = 'hsykjc-0i.myshopify.com'
const STOREFRONT_ACCESS_TOKEN = '1999fe34125fa51bfce5b2a2c82ed9d5'

// Try multiple API versions in order — first one that works wins
const API_VERSIONS = ['2024-04', '2024-01', '2023-10', '2023-07']

function getEndpoint(version: string): string {
  return `https://${SHOPIFY_DOMAIN}/api/${version}/graphql.json`
}

// ─── GraphQL Client ───
async function storefrontFetch<T = any>(
  query: string,
  variables?: Record<string, any>,
  apiVersion?: string,
): Promise<T> {
  const version = apiVersion || API_VERSIONS[0]
  const body: Record<string, any> = { query }
  if (variables) body.variables = variables

  const response = await fetch(getEndpoint(version), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const text = await response.text().catch(() => 'No response body')
    console.error(`[Shopify v${version}] HTTP ${response.status}:`, text)
    throw new Error(`Shopify API v${version} returned ${response.status}`)
  }

  const json = await response.json()

  if (json.errors && json.errors.length > 0) {
    console.error(
      `[Shopify v${version}] GraphQL errors:`,
      JSON.stringify(json.errors, null, 2),
    )
    throw new Error(json.errors.map((e: any) => e.message).join(', '))
  }

  if (!json.data) {
    console.error(`[Shopify v${version}] No data in response:`, json)
    throw new Error('Shopify returned an empty response')
  }

  return json.data as T
}

// ─── Try fetch across multiple API versions ───
async function storefrontFetchWithVersionFallback<T = any>(
  query: string,
  variables?: Record<string, any>,
): Promise<T> {
  let lastError: Error | null = null

  for (const version of API_VERSIONS) {
    try {
      console.log(`[Shopify] Trying API version ${version}...`)
      const result = await storefrontFetch<T>(query, variables, version)
      console.log(`[Shopify] Success with API version ${version}`)
      return result
    } catch (err: any) {
      console.warn(`[Shopify] Version ${version} failed:`, err.message)
      lastError = err
    }
  }

  throw lastError || new Error('All Shopify API versions failed')
}

// ─── Fetch Products ───
const PRODUCTS_QUERY = `
  {
    products(first: 50) {
      edges {
        node {
          id
          title
          description
          tags
          images(first: 1) {
            edges {
              node {
                url
              }
            }
          }
          variants(first: 1) {
            edges {
              node {
                id
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    }
  }
`

export async function fetchProducts(): Promise<Cookie[]> {
  const data = await storefrontFetchWithVersionFallback<any>(PRODUCTS_QUERY)

  if (!data.products?.edges) {
    console.error('[Shopify] Unexpected response shape:', data)
    throw new Error('No products found in Shopify response')
  }

  return data.products.edges.map(({ node }: any) => {
    const variant = node.variants?.edges?.[0]?.node
    const image = node.images?.edges?.[0]?.node?.url
    const tags = (node.tags || []).map((tag: string) =>
      tag.toLowerCase().replace(/\s+/g, '-'),
    )

    return {
      id: node.id,
      name: (node.title || '').toUpperCase(),
      description: node.description || '',
      image,
      tags,
      price: variant?.price?.amount,
      variantId: variant?.id,
      compareAtPrice: variant?.compareAtPrice?.amount,
      badge: tags.includes('new')
        ? 'NEW'
        : tags.includes('best-seller')
          ? 'BEST SELLER'
          : tags.includes('the-hero')
            ? 'THE HERO'
            : undefined,
    }
  })
}

// ─── Create Cart (replaces deprecated checkoutCreate) ───
const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`

export async function createCheckout(items: Cookie[]): Promise<string> {
  const variantCounts = new Map<string, number>()

  for (const item of items) {
    if (!item.variantId) continue
    variantCounts.set(
      item.variantId,
      (variantCounts.get(item.variantId) || 0) + 1,
    )
  }

  const lines = Array.from(variantCounts.entries()).map(
    ([merchandiseId, quantity]) => ({
      merchandiseId,
      quantity,
    }),
  )

  if (lines.length === 0) {
    throw new Error(
      'No valid product variants in your box. Please refresh the page and try again.',
    )
  }

  const data = await storefrontFetchWithVersionFallback<any>(
    CART_CREATE_MUTATION,
    { input: { lines } },
  )

  const { cart, userErrors } = data.cartCreate || {}

  if (userErrors && userErrors.length > 0) {
    const messages = userErrors.map((e: any) => e.message).join('; ')
    console.error('[Shopify] Cart creation errors:', userErrors)
    throw new Error(messages)
  }

  if (!cart?.checkoutUrl) {
    console.error('[Shopify] No checkoutUrl in cart response:', data)
    throw new Error('Shopify did not return a checkout URL. Please try again.')
  }

  return cart.checkoutUrl
}
